import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Paper,
  Stepper,
  Step,
  StepLabel,
  CircularProgress
} from '@mui/material';
import {
  Email as EmailIcon,
  Lock as LockIcon,
  PersonAdd as PersonAddIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as PendingIcon,
  Cancel as RejectedIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import axios from '../api/axios';

// Steps: 0 = email, 1 = password/register/set-password, 2 = success/pending
const STEP_EMAIL = 0;
const STEP_ACTION = 1;
const STEP_RESULT = 2;

function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login, user } = useAuth();

  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');

  // UI state
  const [step, setStep] = useState(STEP_EMAIL);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  // User status from check-email
  const [userExists, setUserExists] = useState(false);
  const [userStatus, setUserStatus] = useState(null);
  const [passwordSet, setPasswordSet] = useState(false);

  useEffect(() => {
    if (user) {
      navigate('/');
    }
  }, [user, navigate]);

  // Check if first superuser needs password
  useEffect(() => {
    const checkInitialSetup = async () => {
      try {
        const response = await axios.get('/auth/check-password-required');
        if (response.data.password_required) {
          setEmail(response.data.email);
          setUserExists(true);
          setUserStatus('approved');
          setPasswordSet(false);
          setStep(STEP_ACTION);
        }
      } catch (err) {
        console.error('Error checking initial setup:', err);
      }
    };
    checkInitialSetup();
  }, []);

  // Step 1: Check email
  const handleCheckEmail = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post('/auth/check-email', { email });
      const data = response.data;

      setUserExists(data.exists);
      setUserStatus(data.status);
      setPasswordSet(data.password_set);
      setMessage(data.message);

      if (data.exists && data.status === 'pending') {
        // Show pending message
        setStep(STEP_RESULT);
      } else if (data.exists && data.status === 'rejected') {
        // Show rejected, allow re-register
        setStep(STEP_ACTION);
      } else {
        // Move to next step (login, register, or set password)
        setStep(STEP_ACTION);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка проверки email');
    } finally {
      setLoading(false);
    }
  };

  // Step 2a: Login with password
  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка входа');
    } finally {
      setLoading(false);
    }
  };

  // Step 2b: Register (send application)
  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post('/auth/register', {
        email,
        full_name: fullName
      });

      setMessage(response.data.message);
      setUserStatus('pending');
      setStep(STEP_RESULT);
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка регистрации');
    } finally {
      setLoading(false);
    }
  };

  // Step 2c: Set first password (after approval)
  const handleSetPassword = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Пароли не совпадают');
      return;
    }

    if (password.length < 8) {
      setError('Пароль должен быть не менее 8 символов');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post('/auth/set-first-password', {
        email,
        password,
        confirm_password: confirmPassword
      });

      // Auto-login after setting password
      localStorage.setItem('access_token', response.data.access_token);
      window.location.href = '/';
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка установки пароля');
    } finally {
      setLoading(false);
    }
  };

  // Go back to email step
  const handleBack = () => {
    setStep(STEP_EMAIL);
    setPassword('');
    setConfirmPassword('');
    setFullName('');
    setError('');
    setMessage('');
  };

  // Render email input step
  const renderEmailStep = () => (
    <Box component="form" onSubmit={handleCheckEmail} sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <EmailIcon sx={{ mr: 1, color: 'primary.main' }} />
        <Typography variant="h6">Введите ваш email</Typography>
      </Box>

      <TextField
        margin="normal"
        required
        fullWidth
        label="Email"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        autoFocus
        disabled={loading}
      />

      <Button
        type="submit"
        fullWidth
        variant="contained"
        sx={{ mt: 3 }}
        disabled={loading || !email}
      >
        {loading ? <CircularProgress size={24} /> : 'Далее'}
      </Button>
    </Box>
  );

  // Render action step (login, register, or set password)
  const renderActionStep = () => {
    // User exists and approved with password set - login
    if (userExists && userStatus === 'approved' && passwordSet) {
      return (
        <Box component="form" onSubmit={handleLogin} sx={{ width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <LockIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">Введите пароль</Typography>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {email}
          </Typography>

          <TextField
            margin="normal"
            required
            fullWidth
            label="Пароль"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
            disabled={loading}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3 }}
            disabled={loading || !password}
          >
            {loading ? <CircularProgress size={24} /> : 'Войти'}
          </Button>

          <Button
            fullWidth
            variant="text"
            sx={{ mt: 1 }}
            onClick={handleBack}
            disabled={loading}
          >
            Другой email
          </Button>
        </Box>
      );
    }

    // User exists and approved but no password - set password
    if (userExists && userStatus === 'approved' && !passwordSet) {
      return (
        <Box component="form" onSubmit={handleSetPassword} sx={{ width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <LockIcon sx={{ mr: 1, color: 'success.main' }} />
            <Typography variant="h6">Установите пароль</Typography>
          </Box>

          <Alert severity="success" sx={{ mb: 2 }}>
            Ваша заявка одобрена! Установите пароль для входа.
          </Alert>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {email}
          </Typography>

          <TextField
            margin="normal"
            required
            fullWidth
            label="Пароль"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
            disabled={loading}
            helperText="Минимум 8 символов"
          />

          <TextField
            margin="normal"
            required
            fullWidth
            label="Подтвердите пароль"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={loading}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="success"
            sx={{ mt: 3 }}
            disabled={loading || !password || !confirmPassword}
          >
            {loading ? <CircularProgress size={24} /> : 'Установить пароль и войти'}
          </Button>
        </Box>
      );
    }

    // User doesn't exist or rejected - register
    return (
      <Box component="form" onSubmit={handleRegister} sx={{ width: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <PersonAddIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6">Заявка на регистрацию</Typography>
        </Box>

        {userStatus === 'rejected' && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Ваша предыдущая заявка была отклонена. Вы можете отправить новую.
          </Alert>
        )}

        {!userExists && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Email не зарегистрирован. Отправьте заявку на регистрацию.
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {email}
        </Typography>

        <TextField
          margin="normal"
          required
          fullWidth
          label="Ваше имя"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          autoFocus
          disabled={loading}
          helperText="Укажите ваше реальное имя"
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          sx={{ mt: 3 }}
          disabled={loading || !fullName}
        >
          {loading ? <CircularProgress size={24} /> : 'Отправить заявку'}
        </Button>

        <Button
          fullWidth
          variant="text"
          sx={{ mt: 1 }}
          onClick={handleBack}
          disabled={loading}
        >
          Другой email
        </Button>
      </Box>
    );
  };

  // Render result step (pending or success)
  const renderResultStep = () => (
    <Box sx={{ width: '100%', textAlign: 'center' }}>
      {userStatus === 'pending' && (
        <>
          <PendingIcon sx={{ fontSize: 64, color: 'warning.main', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Заявка отправлена
          </Typography>
          <Alert severity="info" sx={{ mb: 2, textAlign: 'left' }}>
            Ваша заявка на регистрацию отправлена и ожидает одобрения администратора.
            После одобрения вы сможете установить пароль и войти в систему.
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Email: {email}
          </Typography>
        </>
      )}

      <Button
        fullWidth
        variant="outlined"
        sx={{ mt: 3 }}
        onClick={handleBack}
      >
        Вернуться к входу
      </Button>
    </Box>
  );

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Typography component="h1" variant="h4" gutterBottom>
          Система инвентаризации
        </Typography>

        <Paper sx={{ p: 4, width: '100%', mt: 2 }}>
          {/* Stepper */}
          <Stepper activeStep={step} sx={{ mb: 4 }}>
            <Step>
              <StepLabel>Email</StepLabel>
            </Step>
            <Step>
              <StepLabel>
                {userExists && passwordSet ? 'Пароль' :
                 userExists && !passwordSet ? 'Новый пароль' : 'Регистрация'}
              </StepLabel>
            </Step>
            <Step>
              <StepLabel>Готово</StepLabel>
            </Step>
          </Stepper>

          {/* Error display */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {/* Step content */}
          {step === STEP_EMAIL && renderEmailStep()}
          {step === STEP_ACTION && renderActionStep()}
          {step === STEP_RESULT && renderResultStep()}
        </Paper>
      </Box>
    </Container>
  );
}

export default Login;
